<template>
  <span>
    <b-button
          v-if="buttonType == 'small'"
          variant="edit"
          qid="users-create-button"
          @click="showInviteModal()"
          :disabled="buttonDisabled"
    >
      <span v-if="!buttonDisabled">
        <font-awesome-icon icon="plus-circle"/>
      </span>
      <span v-if="buttonDisabled">
        <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
      </span>

    </b-button>

    <!-- buttonType 'none': the parent opens the modal with showInviteModal() -->
    <b-button
          v-else-if="buttonType != 'none'"
          variant="success"
          class="float-right"
          qid="users-create-button"
          @click="showInviteModal()"
          :disabled="buttonDisabled"
    >
      <span v-if="!buttonDisabled">
        <font-awesome-icon icon="plus-circle"/>{{ $t('maintenance.users.invite_button') }}
      </span>
      <span v-if="buttonDisabled">
        <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>{{ $t('maintenance.users.invite_button') }}
      </span>
    </b-button>


    <!-- One modal for new and existing users; the form adapts once the email is checked (US 9365) -->
    <b-modal ref="invite-user"
             modal-class="v2-modal us-invite-modal"
             hide-footer
             @shown="focusEmail"
    >
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0" qid="invite-user-title">{{ $t('users_page.invite.title') }}</h5>
            <p class="us-modal-head__sub">{{ $t('users_page.invite.subtitle') }}</p>
          </div>
          <button type="button"
                  class="us-icon-btn"
                  :aria-label="$t('administration.users.create.cancel')"
                  @click="hideInviteModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="invite-user-error"
      >
        {{ error }}
        <span v-if="showGetMore" class="text-success cursor-pointer" @click="goToBilling()">
          {{ $t('administration.users.create.get_more_label') }}
        </span>
      </b-alert>

      <b-form @submit.prevent="submit" qid="invite-user-form" class="us-form">
        <b-form-group class="mb-0">
          <label class="us-field-label" for="invite-user-email">{{ $t('users_page.fields.email') }}<span class="mandatory"> *</span></label>
          <b-form-input
            id="invite-user-email"
            ref="email"
            qid="create-user-form-email"
            type="email"
            v-model="userForm.email"
            required
            :placeholder="$t('users_page.invite.email_placeholder')"
            @change="checkEmail"
            @input="onEmailInput"
          ></b-form-input>
        </b-form-group>

        <div v-if="mode === 'existing'" class="us-found" qid="invite-user-existing">
          <span class="us-avatar" :style="{ backgroundImage: 'url(' + emailAvatar + ')' }"></span>
          <span class="us-found__text">
            <b>{{ userForm.email }}</b>
            <span>{{ $t('users_page.invite.existing', { workspace: getClient.name }) }}</span>
          </span>
        </div>

        <div v-if="mode === 'new'" class="us-form__row">
          <b-form-group class="mb-0">
            <label class="us-field-label">{{ $t('users_page.fields.name') }}<span class="mandatory"> *</span></label>
            <b-form-input
              ref="name"
              qid="create-user-form-name"
              type="text"
              v-model="userForm.name"
              required
              :maxLength="255"
              :placeholder="$t('administration.users.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group class="mb-0">
            <label class="us-field-label">{{ $t('users_page.fields.phone') }}</label>
            <b-form-input
              type="text"
              qid="create-user-form-phone"
              v-model="userForm.phone"
              :placeholder="$t('users_page.invite.optional')"
            ></b-form-input>
          </b-form-group>
        </div>

        <b-form-group v-if="mode" class="mb-0">
          <label class="us-field-label">{{ $t('users_page.fields.role') }}<span class="mandatory"> *</span></label>
          <multiselect
              v-model="selectedRole"
              :options="roles"
              track-by="id"
              :allow-empty="false"
              :placeholder="$t('administration.clients.members.select_role_placeholder')"
              label="name_translated"
              :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')"
              qid="create-user-form-role"
          ></multiselect>
        </b-form-group>
        <b-alert v-if="mode && selectedRole && (selectedRole.name === 'Client admin' || selectedRole.name === 'Partition admin')"
                 show
                 variant="warning"
                 class="mb-0"
        >
           {{ $t('administration.users.create.role_cost_notice') }}
        </b-alert>

        <template v-if="mode === 'new'">
          <b-form-group class="mb-0">
            <label class="us-field-label">{{ $t('users_page.sign_in.label') }}<span class="mandatory"> *</span></label>
            <SignInMethodPicker v-model="userForm.authentication"/>
          </b-form-group>
          <!-- UPN is rarely needed and only used for Microsoft sign-in, so it stays folded -->
          <template v-if="userForm.authentication === 'Microsoft'">
            <button type="button"
                    class="us-fold"
                    :aria-expanded="upnOpen ? 'true' : 'false'"
                    qid="create-user-upn-toggle"
                    @click="upnOpen = !upnOpen"
            >
              <font-awesome-icon :icon="upnOpen ? 'angle-down' : 'angle-right'" fixed-width/>
              {{ $t('users_page.fields.upn_toggle') }}
            </button>
            <b-form-group v-if="upnOpen" class="mb-0">
              <b-form-input
                qid="create-user-form-upn-email"
                type="email"
                v-model="userForm.upn_email"
                :placeholder="userForm.email"
                :aria-label="$t('users_page.fields.upn')"
              ></b-form-input>
              <small class="us-hint">{{ $t('users_page.fields.upn_hint') }}</small>
            </b-form-group>
          </template>
        </template>

        <div class="us-modal-foot">
          <span class="us-modal-foot__note">
            <span v-if="mode === 'new'">{{ $t('users_page.invite.new_user') }}</span>
            <span v-if="mode === 'existing'">{{ $t('users_page.invite.existing_user') }}</span>
          </span>
          <b-button variant="secondary" qid="invite-user-cancel-button" @click="hideInviteModal">
            {{ $t('administration.users.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    variant="success"
                    :disabled="busy"
                    qid="invite-user-submit-button">
            <font-awesome-icon v-if="busy" icon="spinner" class="fa-spin mr-1"/>
            <font-awesome-icon v-else-if="mode === 'existing'" icon="plus" class="mr-1"/>
            <font-awesome-icon v-else-if="mode === 'new'" icon="paper-plane" class="mr-1"/>
            <span v-if="mode === 'existing'">{{ $t('users_page.invite.add') }}</span>
            <span v-else-if="mode === 'new'">{{ $t('users_page.invite.send') }}</span>
            <span v-else>{{ $t('users_page.invite.continue') }}</span>
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </span>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {initialsAvatarUrl} from '@/util/userAvatar.js';
  import SignInMethodPicker from '@/components/user/SignInMethodPicker.vue';

  export default {
    name: 'UserInviteModal',
    data: function() {
      return {
        userForm: {
          name: '',
          email: '',
          phone: '',
          authentication: 'Microsoft',
          upn_email: ''
        },
        // null until the email is checked, then 'new' or 'existing'
        mode: null,
        checkedEmail: '',
        error: '',
        roles: [],
        selectedRole: null,
        busy: false,
        upnOpen: false,
        showGetMore: false
      }
    },
    props: {
      buttonType: String,
      buttonDisabled: Boolean,
    },
    components: {
      SignInMethodPicker
    },
    computed: {
      getClient() {
        return this.$store.state.client;
      },
      getClientRoles() {
        return this.$store.state.client.clientRoles;
      },
      emailAvatar() {
        return initialsAvatarUrl({ email: this.userForm.email });
      }
    },
    methods: {
      showInviteModal() {
        let self = this;
        this.error = '';
        this.showGetMore = false;
        this.busy = false;
        this.mode = null;
        this.upnOpen = false;
        this.checkedEmail = '';
        this.userForm = {
          name: '',
          email: '',
          phone: '',
          authentication: 'Microsoft',
          upn_email: ''
        };
        this.setRoles();
        this.selectedRole = null;
        _.forEach(this.roles, function(role) {
          if (role.name == 'Member') {
            self.selectedRole = role;
          }
        });
        this.$refs['invite-user'].show();
      },
      hideInviteModal() {
        this.$refs['invite-user'].hide();
      },
      focusEmail() {
        if (this.$refs.email) {
          this.$refs.email.focus();
        }
      },
      onEmailInput() {
        // A changed email has to be checked again
        if (this.mode && this.userForm.email !== this.checkedEmail) {
          this.mode = null;
        }
      },
      checkEmail() {
        let self = this;
        let email = (this.userForm.email || '').trim();

        if (!email || email === this.checkedEmail || !/^\S+@\S+\.\S+$/.test(email)) {
          return Promise.resolve();
        }

        this.error = '';
        return piincHttp.get('users/email/check', { params:
          {
            email: email,
            client: this.getClient.id
          }
        }).then(function(response) {
          self.checkedEmail = email;
          self.mode = response.data == 1 ? 'existing' : 'new';
          if (self.mode === 'new') {
            self.$nextTick(function() {
              if (self.$refs.name) {
                self.$refs.name.focus();
              }
            });
          }
        }, function(error) {
          self.checkedEmail = '';
          self.mode = null;
          // The email check answers 400 when the user is already in this workspace
          self.error = error.status === 400 ? self.$t('users_page.invite.already_member') : (error.data && error.data.message) || self.$t('users_page.error');
        });
      },
      submit() {
        let self = this;

        if (!this.mode) {
          this.busy = true;
          this.checkEmail().then(function() {
            self.busy = false;
          });
          return;
        }

        this.busy = true;
        this.error = '';

        let data = {
          email: this.userForm.email,
          client: this.getClient.slug,
          role: this.selectedRole ? this.selectedRole.id : undefined
        };
        if (this.mode === 'new') {
          data.name = this.userForm.name;
          data.phone = this.userForm.phone;
          data.authentication = this.userForm.authentication;
          data.upn_email = this.userForm.authentication === 'Microsoft' ? this.userForm.upn_email : '';
        }

        piincHttp.post('users', data).then(function() {
          self.$toastr('success', self.$t(self.mode === 'existing' ? 'users_page.invite.added' : 'administration.users.create.success'));
          self.busy = false;
          self.hideInviteModal();
          self.$emit('reloadUserList');
        }, function(error) {
          self.busy = false;
          self.showGetMore = false;
          self.error = error.data.message;
          if (error.data.message == 'You are not allowed to add member. Please check subscription.') {
            self.error = self.$t('administration.users.create.subscription_error_member');
            if (!self.getClient.parent) {
              self.showGetMore = true;
            }
          }
          if (error.data.message == 'You are not allowed to add admin. Please check subscription.') {
            if (!self.getClient.parent) {
              self.showGetMore = true;
              self.error = self.$t('administration.users.create.subscription_error_admin');
            } else {
              self.error = self.$t('administration.users.create.subscription_error_partition_admin');
            }
          }
          if (error.data.message == 'You are not allowed to add validator. Please check subscription.') {
            self.error = self.$t('administration.users.create.subscription_error_validator');
            if (!self.getClient.parent) {
              self.showGetMore = true;
            }
          }
          if (error.data.errors && error.data.errors.email) {
            self.error = self.error + '\n\n' + error.data.errors.email[0];
          }
        })
      },
      setRoles() {
        let self = this;
        this.roles = [];

        if (this.getClient) {
          if (this.getClient.user_roles[0].name == "Client admin" || this.getClient.user_roles[0].name == "Partition admin") {
            if (this.getClient.parent) {
              _.forEach(this.getClientRoles, function(role) {
                if (role.name != 'Client admin'){
                  self.roles.push(role);
                }
              })
            } else {
              _.forEach(this.getClientRoles, function(role) {
                if (role.name != 'Partition admin'){
                  self.roles.push(role);
                }
              })
            }
          }

          if (this.getClient.user_roles[0].name == "Validator") {
            _.forEach(this.getClientRoles, function(role) {
              if (role.name == 'Validator' || role.name == 'Member'){
                self.roles.push(role);
              }
            })
          }

          if (this.getClient.user_roles[0].name == "Member") {
            _.forEach(this.getClientRoles, function(role) {
              if (role.name == 'Member'){
                self.roles.push(role);
              }
            })
          }
        }

        _.forEach(this.roles, function(role) {
          role.name_translated = self.$t('maintenance.users.role.' + self.$options.filters.slugify(role.name));
        })
      },
      goToBilling() {
        window.location.href = '/client/' + this.getClient.slug + '?tabindex=1';
      }
    }
  }
</script>
